import React from "react";
import { Copy, Download, FileText, RefreshCw, Lightbulb, Wand2, Loader, ArrowRight } from "lucide-react";

export default function TailoredResumeTab({
  tailored,
  tweaks,
  copied,
  onCopy,
  onDownloadPdf,
  onDownloadDocx,
  onOpenRetailor,
  onOpenInStudio,
  openingStudio = false,
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8" data-testid="dashboard-grid">
      <div className="md:col-span-12 rm-card-static p-6 md:p-8" data-testid="tailored-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: "#10B981", color: "white", border: "3px solid #0F1B2D" }}
            >
              <Lightbulb size={20} strokeWidth={2.5} />
            </div>
            <div className="rm-label">Tailored Resume</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={onCopy}
              className="rm-btn rm-btn-ghost text-xs px-3 py-2"
              data-testid="copy-resume-btn"
            >
              <Copy size={14} /> {copied ? "Copied!" : "Copy"}
            </button>
            <button
              onClick={onDownloadPdf}
              className="rm-btn rm-btn-primary text-xs px-3 py-2"
              data-testid="download-pdf-btn"
            >
              <Download size={14} /> PDF
            </button>
            <button
              onClick={onDownloadDocx}
              className="rm-btn rm-btn-secondary text-xs px-3 py-2"
              data-testid="download-docx-btn"
            >
              <FileText size={14} /> DOCX
            </button>
            <button
              onClick={onOpenRetailor}
              className="rm-btn rm-btn-dark text-xs px-3 py-2"
              data-testid="retailor-btn"
            >
              <RefreshCw size={14} /> Re-tailor
            </button>
            {onOpenInStudio && (
              <button
                onClick={onOpenInStudio}
                disabled={openingStudio}
                className="rm-btn text-xs px-3 py-2"
                style={{
                  background: "linear-gradient(135deg,#FF6A00 0%,#B45309 100%)",
                  color: "white",
                  border: "3px solid #0F1B2D",
                  boxShadow: "2px 2px 0 0 #0F1B2D",
                  fontWeight: 700,
                }}
                data-testid="open-in-studio-btn"
                title="Open this resume in Studio to tweak it with live ATS scoring and auto-patch suggestions"
              >
                {openingStudio ? <Loader size={14} className="animate-spin" /> : <Wand2 size={14} />}
                {openingStudio ? "Opening…" : "Tweak in Studio"} <ArrowRight size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {tailored.headline && (
            <div className="font-display text-2xl uppercase leading-tight">{tailored.headline}</div>
          )}
          {tailored.summary && (
            <div>
              <div className="rm-label mb-1">Summary</div>
              <p className="text-sm">{tailored.summary}</p>
            </div>
          )}
          {(tailored.key_skills || []).length > 0 && (
            <div>
              <div className="rm-label mb-2">Key skills</div>
              <div className="flex flex-wrap gap-2">
                {tailored.key_skills.map((s, i) => (
                  <span key={i} className="rm-tag">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(tailored.experience || []).length > 0 && (
            <div>
              <div className="rm-label mb-2">Experience</div>
              <div className="space-y-3">
                {tailored.experience.map((exp, i) => (
                  <div key={i} className="p-3 rounded-md" style={{ border: "3px solid #0F1B2D" }}>
                    <div className="font-bold text-sm">
                      {exp.title}{" "}
                      <span className="font-normal" style={{ color: "#475569" }}>
                        — {exp.company}
                      </span>
                    </div>
                    <div
                      className="font-mono-rm text-xs uppercase tracking-widest mt-1"
                      style={{ color: "#475569" }}
                    >
                      {exp.dates}
                    </div>
                    <ul className="mt-2 space-y-1 text-sm">
                      {(exp.bullets || []).map((b, j) => (
                        <li key={j}>▸ {b}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(tailored.education || []).length > 0 && (
            <div>
              <div className="rm-label mb-1">Education</div>
              <ul className="text-sm space-y-1">
                {tailored.education.map((ed, i) => (
                  <li key={i}>
                    {ed.degree} — {ed.school}{" "}
                    <span style={{ color: "#475569" }}>({ed.dates})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(tailored.additional || []).length > 0 && (
            <div>
              <div className="rm-label mb-1">Additional</div>
              <ul className="text-sm space-y-1">
                {tailored.additional.map((a, i) => (
                  <li key={i}>• {a}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {tweaks.length > 0 && (
        <div
          className="md:col-span-12 rm-card-static p-6 md:p-8"
          style={{ background: "#0F1B2D", color: "white" }}
          data-testid="tweaks-card"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div className="rm-tag" style={{ background: "#FFC145", borderColor: "#FFC145" }}>
              Suggested Tweaks
            </div>
            {onOpenInStudio && (
              <button
                onClick={onOpenInStudio}
                disabled={openingStudio}
                className="rm-btn text-xs px-3 py-2"
                style={{
                  background: "#FFC145",
                  color: "#0F1B2D",
                  border: "3px solid #FFC145",
                  fontWeight: 800,
                  letterSpacing: "0.02em",
                }}
                data-testid="tweaks-apply-in-studio-btn"
                title="Open in Studio to apply these tweaks line-by-line with live ATS scoring"
              >
                {openingStudio ? <Loader size={14} className="animate-spin" /> : <Wand2 size={14} />}
                {openingStudio ? "Opening…" : "Apply in Studio"} <ArrowRight size={12} />
              </button>
            )}
          </div>
          <h2 className="font-display text-2xl md:text-3xl uppercase mb-2">
            Final polish from <span style={{ color: "#FFC145" }}>Resmo.</span>
          </h2>
          <p
            className="font-mono-rm text-xs uppercase tracking-widest mb-5"
            style={{ color: "#94A3B8" }}
          >
            These need a human touch — Studio lets you apply them one click at a time.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tweaks.map((t, i) => (
              <div
                key={i}
                className="p-4 rounded-xl"
                style={{ background: "white", color: "#0F1B2D", border: "3px solid #FFC145" }}
              >
                <div className="rm-label mb-1">{t.area}</div>
                <div className="text-sm">{t.suggestion}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
